import React, { useEffect, useMemo, useState } from 'react'
import { Range } from 'slate'
import { useSlate, useSlateSelection } from 'slate-react'

import DropDown from 'components/uiKit/Dropdown'
import { BABBLE_TOOLTIP_PORTAL } from 'constants/portals'

import type { ToolbarForm } from '../RichTextTypes'
import { isAnnotationActive, isCrossLinkActive, isLinkActive } from '../inline/withInline'
import { getSetToolbarForm, getToolbarForm, useRichTextContext } from '../useRichTextContext'
import Toolbar from './Toolbar'
import AiForm from './components/AiForm'
import AnnotationForm from './components/AnnotationForm'
import CrossLinkForm from './components/CrossLinkForm'
import LinkForm from './components/LinkForm'
import { useGetOffsetToSelectedText } from './useToolbarPosition'

const FORM_MAP: Record<ToolbarForm, React.FC> = {
  ai: AiForm,
  link: LinkForm,
  annotation: AnnotationForm,
  crossLink: CrossLinkForm,
  default: Toolbar,
}

const BubbleToolbar: React.FC = () => {
  const [visible, setVisible] = useState(false)
  const editor = useSlate()
  const selection = useSlateSelection()
  const toolbarForm = useRichTextContext(getToolbarForm)
  const setToolbarForm = useRichTextContext(getSetToolbarForm)
  const { mainAxis, crossAxis, ref } = useGetOffsetToSelectedText()

  const popupContainer = useMemo(
    () => document.getElementById(BABBLE_TOOLTIP_PORTAL) || document.body,
    [],
  )

  useEffect(() => {
    if (selection && !Range.isCollapsed(selection)) {
      setToolbarForm('default')
    } else if (isLinkActive(editor)) {
      setToolbarForm('link')
    } else if (isCrossLinkActive(editor)) {
      setToolbarForm('crossLink')
    } else if (isAnnotationActive(editor)) {
      setToolbarForm('annotation')
    } else {
      setToolbarForm(null)
    }
  }, [editor, selection, setToolbarForm])

  useEffect(() => {
    setVisible(Boolean(toolbarForm))
  }, [toolbarForm])

  if (!toolbarForm) {
    return null
  }

  const Component = FORM_MAP[toolbarForm]
  return (
    <DropDown
      offset={[mainAxis, crossAxis]}
      overlay={<Component />}
      overlayStyle={{ minWidth: 'auto', zIndex: 10001 }}
      popupContainer={popupContainer}
      rootRef={ref}
      styleType='clear'
      visible={visible}
    />
  )
}

BubbleToolbar.displayName = 'BubbleToolbar'

export default React.memo(BubbleToolbar)
